import { SITE_ORIGIN, SITE_NAME, SITE_LOGO_URL } from './config';

export interface FaqPair {
  q: string;
  a: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

const publisher = {
  '@type': 'Organization',
  name: SITE_NAME,
  logo: {
    '@type': 'ImageObject',
    url: SITE_LOGO_URL,
  },
};

export function buildOrganizationSchema(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_ORIGIN,
    logo: {
      '@type': 'ImageObject',
      url: SITE_LOGO_URL,
    },
  };
}

export function buildWebSiteSchema(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_ORIGIN,
    inLanguage: 'pt-BR',
    publisher,
  };
}

export function buildFaqPageSchema(faqs: FaqPair[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };
}

interface ArticleSchemaOptions {
  title: string;
  description: string;
  url: string;
  imageUrl: string;
  datePublished: string;
  dateModified: string;
  category: string;
}

export function buildArticleSchema(
  opts: ArticleSchemaOptions,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: opts.title,
    description: opts.description,
    image: [opts.imageUrl],
    datePublished: opts.datePublished,
    dateModified: opts.dateModified,
    articleSection: opts.category,
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    publisher,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': opts.url,
    },
  };
}

export function buildBreadcrumbListSchema(
  items: BreadcrumbItem[],
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

interface WebPageSchemaOptions {
  title: string;
  description: string;
  url: string;
}

export function buildWebPageSchema(
  opts: WebPageSchemaOptions,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: opts.title,
    description: opts.description,
    url: opts.url,
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
  };
}

const JSON_LD_IDS = [
  'ld-organization',
  'ld-website',
  'ld-faq',
  'ld-article',
  'ld-breadcrumb',
  'ld-webpage',
];

export function injectJsonLd(id: string, data: Record<string, unknown>): void {
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.setAttribute('data-readdy-jsonld', 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

export function clearJsonLd(): void {
  JSON_LD_IDS.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.remove();
    }
  });
}